import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, roleHome } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandMark from "@/components/public/BrandMark";
import { User, Store, Truck } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "customer", label: "Customer", icon: User },
  { value: "laundry_manager", label: "Laundry", icon: Store },
  { value: "driver", label: "Driver", icon: Truck },
];

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const { content } = useSite();
  const a = content?.auth || {};
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = ROLE_OPTIONS.some((r) => r.value === params.get("role")) ? params.get("role") : "customer";
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: initialRole, tc: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && form.role === "driver" && !form.tc) {
      setError("Please read and accept the Terms & Conditions to sign up as a driver.");
      return;
    }
    setLoading(true);
    try {
      const user = isLogin
        ? await login(form.email, form.password)
        : await register({ name: form.name, email: form.email, password: form.password, phone: form.phone, role: form.role });
      toast.success(isLogin ? "Welcome back!" : "Account created!");
      navigate(roleHome(user.role));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="public-site grid min-h-screen lg:grid-cols-2">
      <AuthVisual a={a} />
      <div className="flex items-center justify-center bg-white px-5 py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="w-full max-w-md">
          <div className="mb-10 lg:hidden"><BrandMark testid="auth-logo-mobile" /></div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900" data-testid="auth-title">
            {isLogin ? a.login_title || "Welcome back" : a.register_title || "Create your account"}
          </h1>
          <p className="mt-2 text-slate-500">{isLogin ? a.login_subtitle : a.register_subtitle}</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            {!isLogin && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">I am a…</Label>
                <div className="mt-2 grid grid-cols-3 gap-2 rounded-full bg-slate-100 p-1">
                  {ROLE_OPTIONS.map((r) => (
                    <button type="button" key={r.value} onClick={() => setForm({ ...form, role: r.value })} data-testid={`role-${r.value}`}
                      className={`flex items-center justify-center gap-1.5 rounded-full px-2 py-2.5 text-sm font-bold transition-[background-color,color,box-shadow] ${form.role === r.value ? "bg-white text-brand shadow-md" : "text-slate-500 hover:text-slate-800"}`}>
                      <r.icon className="h-4 w-4" /> {r.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!isLogin && <AuthField id="name" label="Full name" value={form.name} onChange={set("name")} required placeholder="Jane Doe" testid="name-input" />}
            <AuthField id="email" label="Email" type="email" value={form.email} onChange={set("email")} required placeholder="you@example.com" testid="email-input" />
            {!isLogin && <AuthField id="phone" label="Phone (optional)" value={form.phone} onChange={set("phone")} placeholder="07700 900000" testid="phone-input" />}
            <AuthField id="password" label="Password" type="password" value={form.password} onChange={set("password")} required placeholder="••••••••" testid="password-input" />

            {!isLogin && form.role === "driver" && (
              <label className="flex items-start gap-2 text-sm text-slate-600" data-testid="driver-tc-wrap">
                <input type="checkbox" checked={form.tc} onChange={(e) => setForm({ ...form, tc: e.target.checked })} className="mt-0.5 h-4 w-4 accent-[#2563EB]" data-testid="driver-tc-checkbox" />
                <span>I have read and agree to the <Link to="/terms" target="_blank" className="font-semibold text-brand hover:underline">Terms &amp; Conditions</Link>.</span>
              </label>
            )}

            {error && <p className="rounded-2xl bg-destructive/10 px-4 py-2.5 text-sm text-destructive" data-testid="auth-error">{error}</p>}

            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="h-12 w-full rounded-full bg-brand text-base font-bold text-white shadow-lg shadow-brand/25 transition-[background-color,transform] hover:-translate-y-0.5 hover:bg-brand-hover disabled:opacity-60">
              {loading ? "Please wait…" : isLogin ? "Log in" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            {isLogin ? (
              <>New here? <Link to="/register" className="font-bold text-brand hover:underline" data-testid="link-register">Create an account</Link></>
            ) : (
              <>Already have an account? <Link to="/login" className="font-bold text-brand hover:underline" data-testid="link-login">Log in</Link></>
            )}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

function AuthField({ id, label, testid, ...rest }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="font-semibold text-slate-700">{label}</Label>
      <Input id={id} data-testid={testid} className="h-12 rounded-2xl border-slate-200 bg-slate-50 px-4 text-base focus-visible:bg-white" {...rest} />
    </div>
  );
}

function AuthVisual({ a }) {
  return (
    <div className="hero-glow relative hidden overflow-hidden lg:block" data-testid="auth-visual">
      {a.side_image && <img src={a.side_image} alt="Laundry" className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity" />}
      <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
      <div className="relative flex h-full flex-col justify-between p-12 text-white">
        <BrandMark dark testid="auth-logo" />
        <div>
          <h2 className="max-w-md font-heading text-4xl font-extrabold leading-tight tracking-tight xl:text-5xl">{a.side_title}</h2>
          <p className="mt-4 max-w-sm text-white/70">{a.side_subtitle}</p>
        </div>
      </div>
    </div>
  );
}
